import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
// import MaintenanceModal from "../common/MaintenanceModal/MaintenanceModal";

const NewTariffDetail = ({ activeItem }) => {
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [promoCode, setPromoCode] = useState("");
  // const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [promoCodeVerified, setPromoCodeVerified] = useState(false);
  const [promoCodeChecking, setPromoCodeChecking] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoDiscountFixed, setPromoDiscountFixed] = useState(0);
  const [promoData, setPromoData] = useState(null);
  // Update selected duration when activeItem changes
  useEffect(() => {
    if (activeItem?.durations && activeItem.durations.length > 0) {
      setSelectedDuration(activeItem.durations[0].duration_days);
    }
  }, [activeItem]);

  const getDurationLabel = (months) => {
    switch (months) {
      case 1:
        return "1 месяц";
      case 3:
        return "3 месяца";
      case 6:
        return "6 месяцев";
      case 12:
        return "12 месяцев";
      default:
        return `${months} месяц(ев)`;
    }
  };

  // Mirror of backend PromoEngine._duration_to_period — frontend uses months, backend uses days.
  // 1 month (≈30 days) → "monthly", 3 months (≈90 days) → "quarterly", 6+ months → "yearly"
  const durationToPeriod = (months) => {
    if (months <= 1) return "monthly";
    if (months <= 3) return "quarterly";
    return "yearly";
  };

  // Returns true when the verified promo applies to the given duration.
  // If the promo has no billing_cycle restriction (allowed_periods is empty) it applies everywhere.
  const isPromoApplicableFor = (months) => {
    if (!promoCodeVerified || !promoData) return false;
    const allowed = promoData.allowed_periods;
    if (!allowed || allowed.length === 0) return true;
    return allowed.includes(durationToPeriod(months));
  };

  const calculateDiscount = (months) => {
    switch (months) {
      case 1:
        return 0;
      case 3:
        return 10;
      case 6:
        return 20;
      case 12:
        return 30;
      default:
        return 0;
    }
  };

  const calculatePrice = (basePrice, months) => {
    const monthlyPrice = parseInt(basePrice);
    const discount = calculateDiscount(months);
    const totalWithoutDiscount = monthlyPrice * months;
    const discountAmount = (totalWithoutDiscount * discount) / 100;
    let finalPrice = totalWithoutDiscount - discountAmount;
    
    // Apply promo code discount only if the promo applies to this billing period
    let promoDiscountAmount = 0;
    let totalDiscount = discount;
    if (isPromoApplicableFor(months)) {
      if (promoDiscount > 0) {
        const pct = (finalPrice * promoDiscount) / 100;
        promoDiscountAmount += pct;
        finalPrice -= pct;
        totalDiscount = discount + promoDiscount;
      }
      if (promoDiscountFixed > 0) {
        const fixed = Math.min(promoDiscountFixed, finalPrice);
        promoDiscountAmount += fixed;
        finalPrice = Math.max(finalPrice - promoDiscountFixed, 0);
      }
    }

    return {
      finalPrice,
      totalWithoutDiscount,
      savings: discountAmount + promoDiscountAmount,
      monthlyPrice,
      discount: totalDiscount,
      baseDiscount: discount,
      promoDiscountAmount,
    };
  };

  const handleCheckPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Введите промокод", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setPromoCodeChecking(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_API}/payments/check-promo/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ promo_code: promoCode.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok && data.valid) {
        setPromoCodeVerified(true);
        setPromoDiscount(data.discount_percent || 0);
        setPromoDiscountFixed(data.discount_fixed || 0);
        setPromoData(data);
        toast.success(data.message || "Промокод успешно применен!", {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        setPromoCodeVerified(false);
        setPromoDiscount(0);
        setPromoDiscountFixed(0);
        setPromoData(null);
        toast.error(data.error || "Промокод недействителен", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error checking promo code:", error);
      toast.error("Ошибка при проверке промокода", {
        position: "top-center",
        autoClose: 5000,
      });
      setPromoCodeVerified(false);
      setPromoDiscount(0);
      setPromoDiscountFixed(0);
      setPromoData(null);
    } finally {
      setPromoCodeChecking(false);
    }
  };

  // To re-enable maintenance modal: replace handleSelectTariff body with setShowMaintenanceModal(true)
  const handleSelectTariff = () => {
    if (!activeItem) return;

    const selectedDurationData = activeItem.durations.find(
      (d) => d.duration_days === selectedDuration
    );

    if (!selectedDurationData) {
      toast.error("Выбранная длительность недоступна");
      return;
    }

    const priceInfo = calculatePrice(activeItem.base_price, selectedDuration);

    navigate("/checkout", {
      state: {
        tariff_id: selectedDurationData.tariff_id,
        name: `${activeItem.name} — ${getDurationLabel(selectedDuration)}`,
        durationLabel: getDurationLabel(selectedDuration),
        finalPrice: priceInfo.finalPrice,
        originalPrice: priceInfo.totalWithoutDiscount,
        savings: priceInfo.savings,
        // Only send promo code if it's actually applicable to the selected duration
        promoCode: isPromoApplicableFor(selectedDuration) ? promoCode.trim() : "",
      },
    });
  };

  const CheckIcon = () => (
    <svg
      className="tw-w-5 tw-h-5 tw-text-green-500 tw-mr-3 tw-flex-shrink-0"
      fill="currentColor"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );

  const TelegramIcon = () => (
    <svg
      className="tw-w-5 tw-h-5 tw-ml-2 tw-flex-shrink-0"
      viewBox="0 0 24 24"
      fill="#0088cc"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.155.232.171.326.016.094.037.308.02.475z" />
    </svg>
  );

  const ChromeIcon = () => (
    <img
      src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/chrome/chrome-original.svg"
      alt="Chrome"
      className="tw-w-5 tw-h-5 tw-ml-2 tw-flex-shrink-0"
    />
  );

  const CompanyIcon = () => (
    <img
      src="/favicon.ico"
      alt="Company"
      className="tw-w-5 tw-h-5 tw-ml-2 tw-flex-shrink-0"
    />
  );

  const getInstrumentIcon = (instrument) => {
    if (instrument === "Аналитика в Telegram bot") {
      return <TelegramIcon />;
    } else if (instrument === "Аналитика в расширение Chrome") {
      return <ChromeIcon />;
    } else {
      return <CompanyIcon />;
    }
  };

  if (!activeItem) {
    return (
      <div className="tw-w-full lg:tw-w-3/5">
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-8 tw-border tw-border-gray-200">
          <div className="tw-text-center tw-text-gray-500">Выберите тариф</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-w-full lg:tw-w-3/5">
      {activeItem && (
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-8 tw-border tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
            <div>
              <h2 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">
                Тариф {activeItem.name}
              </h2>
              <p className="tw-text-lg tw-text-gray-600">
                Без ограничений по количеству запросов
              </p>
            </div>
          </div>

          <div className="tw-mb-3">
            <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4 tw-flex tw-items-center">
              <span className="tw-w-8 tw-h-8 tw-bg-blue-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-3">
                <svg
                  className="tw-w-4 tw-h-4 tw-text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              Включенные инструменты:
            </h3>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
              {activeItem.instruments.map((instrument, index) => (
                <div
                  key={index}
                  className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-transition-colors tw-duration-200 hover:tw-bg-green-50"
                >
                  <div className="tw-flex tw-items-center">
                    <CheckIcon />
                    <span className="tw-text-gray-800 tw-font-medium">
                      {instrument}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-center">
                    {getInstrumentIcon(instrument)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="tw-mb-6">
            <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4 tw-flex tw-items-center">
              <span className="tw-w-8 tw-h-8 tw-bg-orange-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-3">
                <svg
                  className="tw-w-4 tw-h-4 tw-text-orange-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              Промокод:
            </h3>
            <div className="tw-flex tw-gap-2 tw-mb-2">
              <div className="tw-relative tw-flex-1">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toLowerCase());
                    setPromoCodeVerified(false);
                    setPromoDiscount(0);
                    setPromoDiscountFixed(0);
                    setPromoData(null);
                  }}
                  placeholder="Введите промокод"
                  disabled={promoCodeVerified}
                  className={`tw-w-full tw-px-4 tw-py-3 tw-border-2 tw-rounded-xl tw-text-gray-900 tw-font-medium tw-transition-all tw-duration-200 focus:tw-outline-none placeholder:tw-text-gray-400 ${
                    promoCodeVerified
                      ? "tw-border-green-500 tw-bg-green-50"
                      : "tw-border-gray-300 focus:tw-border-orange-500 focus:tw-ring-4 focus:tw-ring-orange-100"
                  }`}
                />
                {promoCode && !promoCodeVerified && (
                  <button
                    onClick={() => {
                      setPromoCode("");
                      setPromoCodeVerified(false);
                      setPromoDiscount(0);
                      setPromoDiscountFixed(0);
                      setPromoData(null);
                    }}
                    className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                  >
                    <svg
                      className="tw-w-5 tw-h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
                {promoCodeVerified && (
                  <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-green-500">
                    <svg
                      className="tw-w-6 tw-h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <button
                onClick={handleCheckPromoCode}
                disabled={promoCodeChecking || !promoCode.trim() || promoCodeVerified}
                className="tw-px-6 tw-py-3 tw-bg-gradient-to-r tw-from-orange-500 tw-to-orange-600 tw-text-white tw-font-semibold tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-from-orange-600 hover:tw-to-orange-700 hover:tw-shadow-lg focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-orange-500 focus:tw-ring-opacity-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-whitespace-nowrap"
              >
                {promoCodeChecking ? (
                  <svg
                    className="tw-animate-spin tw-h-5 tw-w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="tw-opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="tw-opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Проверить"
                )}
              </button>
            </div>
            {promoCodeVerified && promoData && (
              <div className="tw-p-3 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-mb-2">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <svg
                    className="tw-w-5 tw-h-5 tw-text-green-500 tw-flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="tw-text-sm tw-text-green-700 tw-font-medium">
                    {promoData.message}
                  </span>
                </div>
                {promoData.benefits && promoData.benefits.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-1">
                    {promoData.benefits.map((benefit, i) => (
                      <span
                        key={i}
                        className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-green-100 tw-text-green-800 tw-border tw-border-green-300"
                      >
                        {benefit.label}
                      </span>
                    ))}
                  </div>
                )}
                {promoData.expires_at && (
                  <p className="tw-text-xs tw-text-green-600 tw-mt-2">
                    Действует до:{" "}
                    {new Date(promoData.expires_at).toLocaleDateString("ru-RU")}
                  </p>
                )}
              </div>
            )}
            <p className="tw-text-sm tw-text-gray-500 tw-flex tw-items-center">
              <svg
                className="tw-w-4 tw-h-4 tw-mr-1 tw-text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              Если у вас есть промокод, введите его для получения скидки
            </p>
          </div>

          {/* Duration Selection */}
          <div className="tw-mb-2">
            <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4 tw-flex tw-items-center">
              <span className="tw-w-8 tw-h-8 tw-bg-purple-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-3">
                <svg
                  className="tw-w-4 tw-h-4 tw-text-purple-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              Период подписки:
            </h3>

            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mb-6">
              {activeItem.durations.map((duration) => {
                const discount = calculateDiscount(duration.duration_days);
                const promoApplies = isPromoApplicableFor(duration.duration_days);
                const promoNotApplicable = promoCodeVerified && !promoApplies;
                const totalDiscount = discount + (promoApplies ? promoDiscount : 0);
                const hasPromoDiscount = promoApplies && promoDiscount > 0;

                return (
                  <button
                    key={duration.duration_days}
                    onClick={() => setSelectedDuration(duration.duration_days)}
                    className={`tw-relative tw-p-2 tw-rounded-xl tw-border-2 tw-transition-all tw-duration-200 tw-text-center ${
                      selectedDuration === duration.duration_days
                        ? "tw-border-blue-500 tw-bg-blue-50 tw-shadow-lg tw-transform tw-scale-105"
                        : promoNotApplicable
                        ? "tw-border-gray-200 tw-bg-gray-50 hover:tw-border-gray-300 hover:tw-shadow-md"
                        : "tw-border-gray-200 tw-bg-white hover:tw-border-gray-300 hover:tw-shadow-md"
                    }`}
                  >
                    {(discount > 0 || hasPromoDiscount) && (
                      <div className="tw-absolute -tw-top-2 -tw-right-2">
                        <span className={`tw-text-white tw-text-xs tw-font-bold tw-px-2 tw-py-1 tw-rounded-full tw-shadow-lg tw-transition-all tw-duration-300 ${
                          hasPromoDiscount
                            ? "tw-bg-gradient-to-r tw-from-red-500 tw-to-red-600 tw-animate-pulse"
                            : "tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600"
                        }`}>
                          -{totalDiscount}%
                        </span>
                      </div>
                    )}
                    <div
                      className={`tw-font-semibold ${
                        selectedDuration === duration.duration_days
                          ? "tw-text-blue-700"
                          : "tw-text-gray-900"
                      }`}
                    >
                      {getDurationLabel(duration.duration_days)}
                    </div>
                    {(duration.duration_days > 1 || hasPromoDiscount) && (
                      <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
                        Скидка {totalDiscount}%
                      </div>
                    )}
                    {promoNotApplicable && (
                      <div className="tw-text-xs tw-text-orange-500 tw-mt-1 tw-font-medium">
                        Промокод не применяется
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Price Display */}
            {(() => {
              const priceInfo = calculatePrice(
                activeItem.base_price,
                selectedDuration
              );

              return (
                <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-p-2 tw-rounded-xl tw-border tw-border-blue-200">
                  <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
                    <div className="tw-mb-2">
                      <span className="tw-text-sm tw-text-gray-600 tw-uppercase tw-tracking-wide tw-font-medium">
                        Итоговая стоимость
                      </span>
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                      <div className="tw-text-3xl tw-font-bold tw-text-blue-600">
                        {priceInfo.finalPrice.toLocaleString("ru-RU")} ₸
                      </div>
                      {selectedDuration > 1 && (
                        <div className="tw-text-lg tw-text-gray-500 tw-line-through">
                          {priceInfo.totalWithoutDiscount.toLocaleString(
                            "ru-RU"
                          )}{" "}
                          ₸
                        </div>
                      )}
                    </div>

                    {selectedDuration > 1 && (
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                        <div className="tw-text-sm tw-text-green-600 tw-font-semibold tw-flex tw-items-center">
                          <svg
                            className="tw-w-4 tw-h-4 tw-mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Экономия: {priceInfo.savings.toLocaleString("ru-RU")}{" "}
                          ₸
                        </div>
                        <div className="tw-text-xs tw-text-gray-600">
                          по сравнению с ежемесячной оплатой
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-pt-6 tw-border-t tw-border-gray-200">
            <button
              onClick={handleSelectTariff}
              className="tw-flex-1 tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-text-white tw-font-semibold tw-py-4 tw-px-6 tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-from-blue-700 hover:tw-to-blue-800 hover:tw-shadow-lg hover:tw-transform hover:tw-scale-[1.02] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-blue-500 focus:tw-ring-opacity-50"
            >
              Выбрать тариф
            </button>
          </div>
        </div>
      )}
      {/* {showMaintenanceModal && <MaintenanceModal onClose={() => setShowMaintenanceModal(false)} />} */}
    </div>
  );
};

export default NewTariffDetail;
